interface SocketMessage {
  type: string;
  message?: string;
  userId?: string;
}

const BASE_RETRY_MS = 5000;
const MAX_RETRY_MS = 60000;

/**
 * The one force-logout socket for the tab. App.tsx (page load) and login.tsx (after
 * sign-in) both call connect(); every logout path reaches disconnect() through
 * clearAuthToken() or logoutService.
 *
 * It used to reconnect after every close - including disconnect() and a force-logout -
 * with a lifetime cap of 5 retries that never re-armed, and App.tsx ran a second socket of
 * its own that reconnected forever. So a tab held two sockets, kept them after logout, and
 * the next user to log in in that tab was still registered as the previous one.
 */
class SocketService {
  private socket: WebSocket | null = null;
  private userId: string | null = null;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private url: string;
  private onForceLogoutCallback: (() => void) | null = null;

  constructor() {
    // Get socket URL from environment or use default
    const baseURL = process.env.REACT_APP_API_URL || 'https://newbackend.comply.now';
    // Convert HTTP/HTTPS URL to WebSocket URL
    const wsProtocol = baseURL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = baseURL.replace(/^https?:\/\//, '').replace(/\/$/, '');
    this.url = process.env.REACT_APP_SOCKET_URL || `${wsProtocol}://${wsHost.replace(/:\d+$/, '')}:5041/ws`;

    // Back online: reconnect now instead of waiting out the backoff.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (!this.userId || this.socket) return;
        this.clearRetry();
        this.retryCount = 0;
        this.open();
      });
    }
  }

  connect(userId: string, onForceLogout: () => void): void {
    const id = String(userId ?? '').trim();
    if (!id) return;
    this.onForceLogoutCallback = onForceLogout;

    // Same user, already open / opening / waiting to retry: nothing to do.
    if (this.userId === id && (this.socket || this.retryTimer)) return;

    // A different user in this tab: drop the previous user's socket first.
    if (this.userId !== id) {
      this.closeCurrent();
      this.retryCount = 0;
    }
    this.userId = id;
    this.open();
  }

  disconnect(): void {
    this.userId = null;
    this.retryCount = 0;
    this.onForceLogoutCallback = null;
    this.closeCurrent();
  }

  send(message: SocketMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  private open(): void {
    const userId = this.userId;
    if (!userId) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.scheduleRetry(userId);
      return;
    }
    this.socket = ws;

    ws.onopen = () => {
      if (this.socket !== ws) return;
      this.retryCount = 0;
      ws.send(JSON.stringify({ type: 'connect', userId }));
    };

    ws.onmessage = (event) => {
      try {
        const data: SocketMessage = JSON.parse(event.data);

        if (data.type === 'forceLogout' || data.type === 'logout') {
          const callback = this.onForceLogoutCallback;
          this.disconnect(); // no reconnect as the user who was just logged out
          callback?.();
        } else if (data.message) {
          console.log('Socket message:', data.message);
        }
      } catch (error) {
        console.error('Error parsing socket message:', error);
      }
    };

    ws.onclose = () => {
      // A socket that was replaced or closed on purpose must not touch the live one, nor retry.
      if (this.socket !== ws) return;
      this.socket = null;
      this.scheduleRetry(userId);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  private scheduleRetry(userId: string): void {
    if (this.userId !== userId) return;
    const delay = Math.min(BASE_RETRY_MS * 2 ** this.retryCount, MAX_RETRY_MS);
    this.retryCount++;
    this.clearRetry();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      // Offline: the 'online' listener picks it up instead.
      if (this.userId === userId && !this.socket && navigator.onLine !== false) this.open();
    }, delay);
  }

  // Nulling this.socket first is what stops its onclose from scheduling a retry.
  private closeCurrent(): void {
    this.clearRetry();
    const ws = this.socket;
    this.socket = null;
    if (ws && ws.readyState !== WebSocket.CLOSED) ws.close();
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }
}

const socketService = new SocketService();
export default socketService;
